import { useMemo } from 'react';
import { useQuery, WatchQueryFetchPolicy, ApolloError } from '@apollo/client';
import { GET_CURRENT_TIER_DATA } from '@pass-frontend/common/data-access';
import {
  KeyNamePair,
  Tier_Pass as Tier,
} from '@pass-frontend/data-access/common/edge';
import { ServiceSectorKey } from '@pass-frontend/data-access/common/edge';
import {
  APOLLO_STATE_PROP_NAME,
  getClientAndFetchPolicyFromProps,
} from '@pass-frontend/data-access/state-management/apollo';
import { env } from '@pass-frontend/features/configuration/util';
import { useCurrentUserData } from './use-current-user-data';

const { FF_ENABLE_HOME_DELIVERY_AARP } = env();

export type CurrentTierProps = Pick<
  Tier,
  'tierId' | 'name' | 'features' | 'authTags'
>;

export type UseCurrentTierData = {
  error: ApolloError | false;
  isAuthorizedDigitalFitnessOnly: boolean;
  currentTier: CurrentTierProps | undefined;
  tierId: CurrentTierProps['tierId'] | undefined;
  loading: boolean;
  shouldDisplayDashboard: boolean;
  shouldDisplayOnlineFitness: boolean;
  shouldDisplayGyms: boolean;
  shouldDisplaySocialEvents: boolean;
  shouldDisplayFitbit: boolean;
  shouldDisplayHomeDelivery: boolean;
  shouldDisplayBrainHealth: boolean;
  shouldDisplayFitnessActivities: boolean;
  isQualifiedUser: boolean;
  hasAuthzForFamilyMembership: boolean;
  hasAuthzForFamGov: boolean;
  shouldDisplayFamilyGovCheckbox: boolean;
};

/**
 * TODO PASS-5033 the "should*" permissions should also be based on subscriptionStatus + memberStatus.
 */
export const useCurrentTierData = (pageProps?: {
  [APOLLO_STATE_PROP_NAME]?: unknown;
  // NOTE: This is needed to refresh the currentTierData requested by the browser, specifically for getting into the "qualified" experience
  ssrMode?: boolean;
}): UseCurrentTierData => {
  const {
    currentSponsor,
    isQualifiedUser,
    loading: isLoadingCurrentUserData,
    isCommercial,
  } = useCurrentUserData();

  const { client, fetchPolicy } = pageProps
    ? getClientAndFetchPolicyFromProps(pageProps)
    : {
        client: undefined,
        fetchPolicy:
          // NOTE: this is important to keep so that refetch queries from use-redirect helpers pick up currentTier.id correctly.
          'network-only' as WatchQueryFetchPolicy,
      };

  const { loading, error, data } = useQuery<{
    currentTier: CurrentTierProps;
  }>(GET_CURRENT_TIER_DATA, {
    client,
    fetchPolicy,
    ...(pageProps && 'ssrMode' in pageProps ? { ssr: pageProps?.ssrMode } : {}),
  });

  const { currentTier } = data || {};

  const permissions = useMemo(() => {
    const authKeys: string[] =
      (currentTier?.authTags &&
        currentTier.authTags
          .map((feature: KeyNamePair | null) => feature?.key ?? '')
          .filter((key) => key.length > 0)) ||
      [];

    const isAuthorizedSocialEvents = authKeys.includes(
      'auth:social:classes-and-events',
    );
    const isAuthorizedOnlineFitness = authKeys.includes('auth:online-fitness');

    // TODO PASS-5033 these should * permissions should also be based on subscriptionStatus + memberStatus.
    return {
      isQualifiedUser: isLoadingCurrentUserData ? isQualifiedUser : null,
      shouldDisplayDashboard: !!currentTier,
      shouldDisplayOnlineFitness: authKeys.includes('auth:online-fitness'),
      isAuthorizedDigitalFitnessOnly:
        authKeys.length === 1 && isAuthorizedOnlineFitness,
      shouldDisplayGyms: authKeys.some((fKey: string) =>
        fKey.startsWith('auth:fitness-network'),
      ),
      shouldDisplaySocialEvents: isAuthorizedSocialEvents,
      shouldDisplayFitbit: authKeys.includes('auth:social:fitbit-community'),
      shouldDisplayHomeDelivery:
        // for RA experience mom's meal will be disabled
        authKeys.includes('auth:food-delivery:moms-meals') ||
        authKeys.includes('auth:food-delivery:commercial-shipt') ||
        authKeys.includes('auth:food-delivery:commercial-walmart') ||
        authKeys.includes('auth:food-delivery:commercial-oura') ||
        authKeys.includes('auth:food-delivery:commercial-ozer') ||
        (FF_ENABLE_HOME_DELIVERY_AARP &&
          authKeys.includes('auth:food-delivery:aarp')) ||
        currentSponsor?.rewardsPaymentSystem?.includes('FitnessRewards'),
      shouldDisplayBrainHealth:
        authKeys.includes('auth:cognitive:standard-cognifit') ||
        authKeys.includes('auth:cognitive:buyup-aarp') ||
        authKeys.includes('auth:cognitive:standard-brainhq'),
      shouldDisplayFitnessActivities:
        isAuthorizedSocialEvents &&
        currentSponsor?.serviceSector?.key === ServiceSectorKey.government, // fitness activities tab should be available only if service sector is government and the user has access to social events
      hasAuthzForFamilyMembership: authKeys.includes(
        'auth:one-pass:family-membership',
      ),
      hasAuthzForFamGov: authKeys.includes(
        'auth:one-pass:government-family-membership',
      ),
      shouldDisplayFamilyGovCheckbox: authKeys.includes(
        'auth:fitness-network:government-dependent',
      ),
    };
  }, [currentTier?.authTags, currentSponsor, isQualifiedUser]);

  return {
    ...permissions,
    loading: loading || false,
    error: error || false,
    currentTier,
    tierId: currentTier?.tierId,
    isQualifiedUser,
  };
};
